import { useState, useRef, useEffect, useMemo } from 'react'
import { useLocation } from 'react-router-dom'
import { Box, List, ListItemButton, ListItemIcon, ListItemText, Typography, useTheme, Tooltip } from '@mui/material'
import { match } from 'path-to-regexp'
import { useAppSelector } from 'utils/custom-hooks/useTypedReduxStore'
import { Link } from 'react-router-dom'
import {
  PURCHASES_TAB_ROUTES,
  SALES_TAB_ROUTES,
  CONTRACT_TAB_ROUTES,
  INVENTORY_TAB_ROUTES,
  COMMODITIES_TAB_ROUTES,
  LOAD_BOARD_TAB_ROUTES,
  ASSET_TAB_ROUTES,
  REPORTS_TAB_ROUTES,
  LEDGER_TAB_ROUTES,
  FREIGHT_TAB_ROUTES,
} from 'utils/ui/routes'
import StarIcon from '@mui/icons-material/Star'
import StarBorderIcon from '@mui/icons-material/StarBorder'
import IconButton from '@mui/material/IconButton'
import ExpandLess from '@mui/icons-material/ExpandLess'
import ExpandMore from '@mui/icons-material/ExpandMore'
import Collapse from '@mui/material/Collapse'
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart'
import SellIcon from '@mui/icons-material/Sell'
import DescriptionIcon from '@mui/icons-material/Description'
import InventoryIcon from '@mui/icons-material/Inventory'
import PeopleIcon from '@mui/icons-material/People'
import LocalShippingIcon from '@mui/icons-material/LocalShipping'
import AssignmentIndIcon from '@mui/icons-material/AssignmentInd'
import { PieChart, Warehouse } from '@mui/icons-material_modern'
import AssessmentIcon from '@mui/icons-material/Assessment'
import AccountBalanceIcon from '@mui/icons-material/AccountBalance'
import NavHeader from './NavHeader'
import { ORG_MODE_ROUTES } from 'utils/constants'
import { AppTab } from 'AppRoutes'
import useRbac from 'utils/custom-hooks/useRbac'
import useScrollPersistence from 'utils/custom-hooks/useScrollPersistence'
import React from 'react'

interface CategoryNavProps {
  isEnabled?: boolean
  toggleNavStyle: () => void
  useCategoryNav: boolean
  toggleTheme: () => void
  currentTheme: string
  showSettings: boolean
}

interface RouteItem extends AppTab {
  icon?: React.ReactNode
  isFavorite?: boolean
  originalCategory?: string
}

interface RouteCategory {
  title: string
  routes: RouteItem[]
  basePath: string
}

const getFullRoute = (categoryTitle: string, route: string) =>
  route.startsWith('/') ? route : `/app/${categoryTitle.toLowerCase()}/${route}`

const getCategoryIcon = (categoryTitle: string) => {
  const iconProps = { sx: { fontSize: '1rem', mr: 1 } }

  switch (categoryTitle) {
    case 'PURCHASES':
      return <ShoppingCartIcon {...iconProps} />
    case 'SALES':
      return <SellIcon {...iconProps} />
    case 'CONTRACTS':
      return <DescriptionIcon {...iconProps} />
    case 'INVENTORY':
      return <InventoryIcon {...iconProps} />
    case 'CUSTOMERS':
      return <PeopleIcon {...iconProps} />
    case 'COMMODITIES':
      return <PieChart {...iconProps} />
    case 'FREIGHT':
      return <LocalShippingIcon {...iconProps} />
    case 'LOAD-BOARD':
      return <AssignmentIndIcon {...iconProps} />
    case 'ASSETS':
      return <Warehouse {...iconProps} />
    case 'REPORTS':
      return <AssessmentIcon {...iconProps} />
    case 'LEDGER':
      return <AccountBalanceIcon {...iconProps} />
    case 'FAVORITES':
      return <StarIcon {...iconProps} />
    default:
      return null
  }
}

const getCategories = (enableBatchBills: boolean): RouteCategory[] => [
  {
    title: 'PURCHASES',
    routes: PURCHASES_TAB_ROUTES.filter(tab => (enableBatchBills ? true : tab.route !== 'batch-bills')),
    basePath: 'purchases',
  },
  { title: 'SALES', routes: SALES_TAB_ROUTES, basePath: 'sales' },
  { title: 'CONTRACTS', routes: CONTRACT_TAB_ROUTES, basePath: 'contracts' },
  { title: 'INVENTORY', routes: INVENTORY_TAB_ROUTES, basePath: 'inventory' },
  {
    title: 'CUSTOMERS',
    routes: [{ title: 'Customers', route: '/app/customers', matchPath: '/app/customers/:id*' }],
    basePath: 'customers',
  },
  { title: 'COMMODITIES', routes: COMMODITIES_TAB_ROUTES, basePath: 'commodities' },
  { title: 'FREIGHT', routes: FREIGHT_TAB_ROUTES, basePath: 'freight' },
  { title: 'LOAD-BOARD', routes: LOAD_BOARD_TAB_ROUTES, basePath: 'load-board' },
  { title: 'ASSETS', routes: ASSET_TAB_ROUTES, basePath: 'assets' },
  { title: 'REPORTS', routes: REPORTS_TAB_ROUTES, basePath: 'reports' },
  { title: 'LEDGER', routes: LEDGER_TAB_ROUTES, basePath: 'ledger' },
]

const CategoryNav: React.FC<CategoryNavProps> = ({
  isEnabled = true,
  toggleNavStyle,
  useCategoryNav,
  toggleTheme,
  currentTheme,
  showSettings,
}) => {
  const theme = useTheme()
  const location = useLocation()
  const { showSideMenuText, inOrgMode } = useAppSelector(state => state.ui)
  const { user } = useAppSelector(state => state.auth)
  const { variables } = useAppSelector(state => state.featureFlags)
  const { enableBatchBills } = variables || {}
  const { roles } = useAppSelector(state => state.permissions)
  const yardSettingsLoaded = useAppSelector(state => state.settings.yardSettingsLoaded)
  const isYardAdminOrManager = !!roles?.find(r => r.roleName === 'yardAdmin' || r.roleName === 'yardManager')
  const { rbacMap } = useRbac()

  const [isHovering, setIsHovering] = useState(false)
  const [favoriteRoutes, setFavoriteRoutes] = useState<string[]>(() =>
    JSON.parse(localStorage.getItem(`favoriteRoutes-${user?.attributes?.sub}`) || '[]'),
  )
  const [collapsedSections, setCollapsedSections] = useState<string[]>(() =>
    JSON.parse(localStorage.getItem('collapsedNavSections') || '[]'),
  )
  const hoverTimeoutRef = useRef<NodeJS.Timeout>()

  // Use the custom hook for scroll persistence (without parameter)
  const scrollContainerRef = useScrollPersistence()

  const filteredCategories = useMemo(() => {
    if (!yardSettingsLoaded) {
      return []
    }
    const categories = getCategories(enableBatchBills)
    return categories
      .filter(category => {
        if (rbacMap[category.basePath] == false) return false
        const featureFlag = `appRoute${category.title
          .split('-')
          .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
          .join('')}`
        if (!variables[featureFlag]) return false
        if (inOrgMode) {
          const normalizedTitle = category.title
            .split('-')
            .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
            .join(' ')
          return ORG_MODE_ROUTES.includes(normalizedTitle)
        }
        return true
      })
      .map(category => ({
        ...category,
        routes: category.routes.filter(routeItem => {
          const rbacKey = `${category.basePath}/${routeItem.route}`
          if (rbacMap[rbacKey] == false) return false
          return true
        }),
      }))
      .filter(category => category.routes.length > 0)
  }, [inOrgMode, variables, enableBatchBills, isYardAdminOrManager, rbacMap, yardSettingsLoaded])

  const categoriesWithFavorites = useMemo(() => {
    const favoritesCategory = {
      title: 'FAVORITES',
      routes: filteredCategories
        .flatMap(category =>
          category.routes.map(route => ({
            ...route,
            originalCategory: category.title,
            isFavorite: true,
          })),
        )
        .filter(route => favoriteRoutes.includes(route.route)),
    }
    return favoritesCategory.routes.length > 0 ? [favoritesCategory, ...filteredCategories] : filteredCategories
  }, [filteredCategories, favoriteRoutes])

  const handleToggleFavorite = (e: React.MouseEvent, route: RouteItem) => {
    e.preventDefault()
    const newFavorites = route.isFavorite
      ? favoriteRoutes.filter(r => r !== route.route)
      : [...favoriteRoutes, route.route]
    setFavoriteRoutes(newFavorites)
    localStorage.setItem(`favoriteRoutes-${user?.attributes?.sub}`, JSON.stringify(newFavorites))
  }

  const handleToggleSection = (categoryTitle: string) => {
    const newCollapsedSections = collapsedSections.includes(categoryTitle)
      ? collapsedSections.filter(title => title !== categoryTitle)
      : [...collapsedSections, categoryTitle]
    setCollapsedSections(newCollapsedSections)
    localStorage.setItem('collapsedNavSections', JSON.stringify(newCollapsedSections))
  }

  const handleMouseEnter = () => {
    if (!showSideMenuText) {
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current)
      }
      setIsHovering(true)
    }
  }

  const handleMouseLeave = (e: React.MouseEvent) => {
    const relatedTarget = e.relatedTarget as Element | null | undefined
    if (!relatedTarget || typeof (relatedTarget as any).closest !== 'function') {
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current)
      }
      hoverTimeoutRef.current = setTimeout(() => setIsHovering(false), 150)
      return
    }

    if (!relatedTarget.closest('#nav-side-bar')) {
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current)
      }
      hoverTimeoutRef.current = setTimeout(() => setIsHovering(false), 150)
    }
  }

  useEffect(() => {
    return () => {
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current)
      }
    }
  }, [])

  if (!isEnabled) return null

  return (
    <>
      <Box
        sx={{
          position: 'fixed',
          left: 0,
          top: 0,
          width: '24px',
          height: '100vh',
          zIndex: 9999,
        }}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      />
      {(showSideMenuText || (!showSideMenuText && isHovering)) && (
        <Box
          sx={{
            position: showSideMenuText ? 'relative' : 'fixed',
            left: 0,
            top: 0,
            zIndex: 5,
            boxShadow: !showSideMenuText ? 8 : 'none',
            backgroundColor: 'background.color',
          }}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        >
          <Box
            id="nav-side-bar"
            sx={{
              height: '100vh',
              borderRight: 1,
              borderColor: 'divider',
              backgroundColor: 'primary.color',
              width: 225,
              transition: 'width 0.5s ease-in-out',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <NavHeader
              onClose={() => setIsHovering(false)}
              toggleNavStyle={toggleNavStyle}
              useCategoryNav={useCategoryNav}
              toggleTheme={toggleTheme}
              showSettings={showSettings}
              currentTheme={currentTheme}
            />
            <Box
              ref={scrollContainerRef}
              sx={{
                overflow: 'auto',
                flex: 1,
                pl: 2,
              }}
            >
              {categoriesWithFavorites.map(category => (
                <Box key={category.title} sx={{ mb: 1 }}>
                  <Box
                    sx={{
                      borderRadius: '4px',
                      overflow: 'hidden',
                    }}
                  >
                    <ListItemButton
                      onClick={() => handleToggleSection(category.title)}
                      sx={{
                        minHeight: 40,
                        color: theme.palette.text.secondary,
                        px: 0.5,
                        '&:hover': {
                          backgroundColor: theme.palette.action.hover,
                        },
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
                        {getCategoryIcon(category.title)}
                        <Typography
                          variant="subtitle2"
                          sx={{
                            px: 0,
                            display: 'block',
                            color: theme.palette.text.primary,
                            fontWeight: 700,
                            fontSize: '0.8rem',
                            letterSpacing: '0.5px',
                            textTransform: 'uppercase',
                          }}
                        >
                          {category.title}
                        </Typography>
                      </Box>
                      <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center' }}>
                        {collapsedSections.includes(category.title) ? (
                          <ExpandMore sx={{ fontSize: '1.2rem' }} />
                        ) : (
                          <ExpandLess sx={{ fontSize: '1.2rem' }} />
                        )}
                      </Box>
                    </ListItemButton>
                    <Collapse in={!collapsedSections.includes(category.title)} timeout="auto" unmountOnExit>
                      <Box
                        sx={{
                          py: 0,
                        }}
                      >
                        <List component="nav" disablePadding>
                          {category.routes.map(route => {
                            const matcher = route?.matchPath && match(route.matchPath)
                            const isSelected = matcher && !!matcher(location.pathname)
                            const isFavorited = favoriteRoutes.includes(route.route)
                            return (
                              <Link
                                key={getFullRoute(route.originalCategory || category.title, route.route)}
                                to={getFullRoute(route.originalCategory || category.title, route.route)}
                                style={{ textDecoration: 'none' }}
                              >
                                <ListItemButton
                                  sx={{
                                    pl: 3.5,
                                    pr: 1.5,
                                    py: 1,
                                    height: 32,
                                    width: '100%',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'flex-start',
                                    backgroundColor: isSelected ? theme.palette.primary.dark : 'transparent',
                                    mx: 0,
                                    mb: 0,
                                    borderRadius: 1,
                                    fontWeight: isSelected ? 600 : 400,
                                    color: isSelected ? theme.palette.text.primary : theme.palette.text.primary,
                                    position: 'relative',
                                    '&::before': {
                                      content: '""',
                                      position: 'absolute',
                                      left: '10px',
                                      top: 0,
                                      bottom: 0,
                                      width: '2px',
                                      backgroundColor: isSelected ? 'transparent' : theme.palette.divider,
                                    },
                                    '&:hover': {
                                      backgroundColor: isSelected ? theme.palette.primary.dark : 'primary.lightBg',
                                      '& .favorite-icon': {
                                        opacity: 1,
                                      },
                                    },
                                  }}
                                >
                                  {route.icon && (
                                    <ListItemIcon
                                      sx={{
                                        minWidth: 36,
                                        color: isSelected ? '#fff' : theme.palette.text.secondary,
                                      }}
                                    >
                                      {route.icon}
                                    </ListItemIcon>
                                  )}
                                  <ListItemText
                                    primary={route.title}
                                    sx={{
                                      margin: 0,
                                      flex: 1,
                                      '& .MuiListItemText-primary': {
                                        textAlign: 'left',
                                        color: isSelected ? '#fff' : theme.palette.text.secondary,
                                        fontSize: '13px',
                                        fontWeight: isSelected ? 600 : 400,
                                        lineHeight: 1.1,
                                      },
                                    }}
                                  />
                                  <Box
                                    className="favorite-icon"
                                    sx={{
                                      opacity: 0,
                                      transition: 'opacity 0.2s ease',
                                      display: 'flex',
                                      alignItems: 'center',
                                    }}
                                  >
                                    <Tooltip title={isFavorited ? 'Remove from favorites' : 'Add to favorites'}>
                                      <IconButton
                                        size="small"
                                        onClick={e => handleToggleFavorite(e, route)}
                                        sx={{
                                          width: 24,
                                          height: 24,
                                          color: isFavorited
                                            ? theme.palette.warning.main
                                            : theme.palette.text.secondary,
                                          '&:hover': {
                                            backgroundColor: 'rgba(0, 0, 0, 0.08)',
                                          },
                                        }}
                                      >
                                        {isFavorited ? (
                                          <StarIcon sx={{ fontSize: '1rem' }} />
                                        ) : (
                                          <StarBorderIcon sx={{ fontSize: '1rem' }} />
                                        )}
                                      </IconButton>
                                    </Tooltip>
                                  </Box>
                                </ListItemButton>
                              </Link>
                            )
                          })}
                        </List>
                      </Box>
                    </Collapse>
                  </Box>
                </Box>
              ))}
            </Box>
          </Box>
        </Box>
      )}
    </>
  )
}

CategoryNav.defaultProps = {
  isEnabled: true,
}

export default React.memo(CategoryNav)
